import { Link } from "react-router-dom";
import usePageSEO from "@/hooks/usePageSEO";
import JsonLd from "@/components/feature/JsonLd";
import HowWeWork from "@/pages/home/components/HowWeWork";
import ExportGallery from "@/pages/home/components/ExportGallery";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://ghbeauty.kr";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl + "/" },
    { "@type": "ListItem", "position": 2, "name": "Services", "item": siteUrl + "/services" }
  ]
};

const servicesList = [
  {
    no: "01",
    title: "Multi-Brand Sourcing",
    desc: "Access products from 125+ Korean beauty brands through verified domestic supply channels with competitive wholesale pricing.",
    img: "https://storage.readdy-site.link/project_files/35b57ced-d75b-4532-9a59-9b08ce476502/78560f4f-9e46-4c56-bba0-a4b90286aa0d_compressed_-2.webp",
    bullets: ["125+ brand portfolio", "Volume-based wholesale pricing", "Mixed-brand purchase orders"],
  },
  {
    no: "02",
    title: "Wholesale Quotation",
    desc: "Receive detailed wholesale pricing, MOQ guidance, estimated lead times and a recommended shipping plan tailored to your requirements.",
    img: "https://readdy.ai/api/search-image?query=Professional%20editorial%20photograph%20of%20a%20clean%20organized%20desk%20in%20a%20bright%20modern%20Korean%20office%20with%20a%20quotation%20document%20laptop%20and%20cosmetics%20product%20samples%20arranged%20neatly%20on%20a%20warm%20wooden%20surface%2C%20soft%20natural%20daylight%2C%20muted%20ivory%20cream%20and%20warm%20beige%20tones%2C%20no%20text%20no%20logos%2C%20refined%20commercial%20photography&width=900&height=900&seq=gh-service-quotation&orientation=squarish",
    bullets: ["Detailed pricing sheet", "MOQ & lead time guidance", "Shipping cost estimates"],
  },
  {
    no: "03",
    title: "Product Consolidation",
    desc: "Combine products from multiple brands into one consolidated order. We gather, organize and prepare your complete order at our Korean facility.",
    img: "https://storage.readdy-site.link/project_files/35b57ced-d75b-4532-9a59-9b08ce476502/73245f87-031a-4666-a21e-476f625d53f1_compressed_515590e5-b853-4590-9853-cf1d6b731d2f.webp",
    bullets: ["Multi-brand consolidation", "Single shipment", "Cost-efficient grouping"],
  },
  {
    no: "04",
    title: "Inspection & Export Packing",
    desc: "Every product undergoes authenticity verification and expiry-date checks before professional export-grade packing for safe international transit.",
    img: "https://storage.readdy-site.link/project_files/35b57ced-d75b-4532-9a59-9b08ce476502/c15092a4-21ce-4cfe-b5b8-14a8f1c2ff05_compressed_54d680e1-d399-40de-900f-84f0ca30feed.webp",
    bullets: ["Authenticity verification", "Expiry-date checks", "Export-grade packaging"],
  },
  {
    no: "05",
    title: "Export Documentation",
    desc: "We prepare standard export documents including commercial invoices, packing lists and certificates of origin where applicable. Additional regulatory or market-specific documents are coordinated according to the product, brand, destination and importer requirements.",
    img: "https://readdy.ai/api/search-image?query=Professional%20editorial%20close%20up%20of%20export%20documents%20and%20shipping%20paperwork%20neatly%20arranged%20on%20a%20warm%20wooden%20desk%20surface%20with%20a%20pen%2C%20soft%20diffused%20natural%20daylight%2C%20muted%20warm%20ivory%20and%20cream%20tones%2C%20refined%20minimalist%20composition%2C%20no%20text%20no%20logos%2C%20professional%20commercial%20photography&width=900&height=900&seq=gh-service-documentation&orientation=squarish",
    bullets: ["Commercial invoices & packing lists", "Certificate of origin coordination", "Destination-specific documents"],
  },
  {
    no: "06",
    title: "FCL, LCL & Air Freight",
    desc: "Coordinated logistics support provides clearer communication, efficient shipment planning and reliable execution across all freight modes.",
    img: "https://storage.readdy-site.link/project_files/35b57ced-d75b-4532-9a59-9b08ce476502/65aa3aad-5233-4d5f-9ddb-d0099c495d16_compressed_19.webp",
    bullets: ["FCL · LCL · AIR options", "Shipment planning", "Real-time tracking"],
  },
  {
    no: "07",
    title: "Market-Specific Shipping Support",
    desc: "Shipping routes, documentation and delivery terms are arranged according to each destination market and order volume for reliable execution.",
    img: "https://readdy.ai/api/search-image?query=Professional%20editorial%20photograph%20of%20a%20world%20map%20and%20logistics%20planning%20documents%20spread%20across%20a%20bright%20modern%20office%20desk%20with%20warm%20natural%20daylight%2C%20muted%20ivory%20cream%20and%20warm%20beige%20tones%2C%20refined%20global%20trade%20atmosphere%2C%20no%20text%20no%20logos%2C%20professional%20commercial%20photography&width=900&height=900&seq=gh-service-market-shipping&orientation=squarish",
    bullets: ["Market-specific routing", "Documentation per destination", "Delivery term guidance"],
  },
  {
    no: "08",
    title: "After-Sales Communication",
    desc: "One experienced contact stays with you after shipment — supporting reorders, handling questions and ensuring smooth ongoing cooperation.",
    img: "https://storage.readdy-site.link/project_files/35b57ced-d75b-4532-9a59-9b08ce476502/55ed0972-2382-4c38-bd1c-34f3ac0034aa_compressed_GH-4.webp",
    bullets: ["Dedicated contact person", "Reorder management", "Ongoing support"],
  },
];

export default function ServicesPage() {
  usePageSEO({
    title: "K-Beauty Sourcing & Global Logistics | GH Corporation",
  });

  return (
    <>
    <section className="w-full px-6 md:px-12 lg:px-20 pt-28 pb-24 md:pb-32 bg-background-50">
      <JsonLd data={breadcrumbSchema} />
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-accent-500" />
              <span className="font-label text-[11px] tracking-[0.22em] uppercase text-accent-700">
                Export Services
              </span>
            </div>
            <h1 className="font-heading text-foreground-950 text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-light">
              Sourcing, shipping &amp;
              <br />
              <span className="italic">everything in between.</span>
            </h1>
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <p className="text-foreground-700 text-[15px] md:text-base leading-relaxed font-light">
              From the first quotation to the last delivery mile, GH Corporation operates as a single
              accountable partner — so you never coordinate between a sourcing agent and a
              freight forwarder again.
            </p>
          </div>
        </div>

        <div className="mt-14 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {servicesList.map((s) => (
            <article
              key={s.no}
              className="group relative rounded-2xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-accent-300 transition-colors"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className="aspect-square sm:aspect-auto sm:min-h-[320px] overflow-hidden">
                  <img
                    src={s.img}
                    alt={`${s.title} — GH Corporation export service`}
                    title={s.title}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="p-7 md:p-8 flex flex-col">
                  <div className="font-label text-[11px] tracking-[0.3em] text-accent-700">
                    {s.no}
                  </div>
                  <h2 className="mt-3 font-heading text-foreground-950 text-2xl md:text-[28px] leading-tight">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-foreground-700 text-[15px] leading-relaxed font-light">
                    {s.desc}
                  </p>
                  <ul className="mt-6 space-y-2.5">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-center gap-3 text-foreground-800 text-sm"
                      >
                        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                          <i className="ri-check-line text-xs" />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Freight Details + Trade Terms */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { 
              title: "LCL", 
              sub: "Less than Container Load",
              desc: "Cost-effective consolidation for small, trial or mixed-brand orders.",
            },
            { 
              title: "FCL", 
              sub: "Full Container Load",
              desc: "Dedicated container shipments for larger-volume orders and stable replenishment.",
            },
            { 
              title: "AIR", 
              sub: "Air Freight",
              desc: "Faster delivery for urgent launches, samples and time-sensitive products.",
            },
            { 
              title: "Trade Terms", 
              sub: "EXW · FOB · CIF",
              desc: "Support for common trade terms including EXW, FOB and CIF. DAP or DDP may be reviewed depending on destination, product compliance requirements and local import conditions.",
            },
          ].map((m) => (
            <div
              key={m.title}
              className="p-6 rounded-2xl bg-secondary-100 border border-secondary-200 flex flex-col"
            >
              <div className="font-heading text-secondary-950 text-3xl leading-none">
                {m.title}
              </div>
              <div className="mt-1.5 font-label text-[11px] tracking-[0.18em] uppercase text-secondary-800">
                {m.sub}
              </div>
              <p className="mt-4 text-secondary-800 text-[14px] leading-relaxed font-light flex-1">
                {m.desc}
              </p>
            </div>
          ))}
        </div>

        <p className="mt-8 text-foreground-700 text-[14px] leading-relaxed font-light text-center">
          We coordinate palletizing, carton labeling, export-document preparation and shipment routing according to the buyer&apos;s destination and order volume.
        </p>

        <div className="mt-14 flex flex-col md:flex-row md:items-center md:justify-between gap-5 p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70">
          <p className="text-foreground-700 text-[15px] md:text-base">
            Curious about shipping costs or lead times to your country? Send us your
            requirements and we&apos;ll respond with a tailored quote.
          </p>
          <Link
            to="/contact"
            className="whitespace-nowrap cursor-pointer inline-flex items-center gap-3 px-5 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors font-label text-[12px] tracking-[0.18em] uppercase self-start md:self-auto"
          >
            Get a Shipping Quote
            <i className="ri-arrow-right-up-line" />
          </Link>
        </div>
      </div>
    </section>

    <HowWeWork />
    <ExportGallery />
    </>
  );
}